import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Ban, CircleCheck, Send, ShieldCheck, ShieldX, FileImage } from 'lucide-react';
import api from '../lib/api';
import { formatApiError, formatZAR } from '../lib/format';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';

const KYC_LABEL = { verified: 'Verified', pending: 'Pending', rejected: 'Rejected', not_submitted: 'Not submitted' };

function AuthedImage({ url, alt }) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let revoke = '';
    if (!url) return;
    const backend = process.env.REACT_APP_BACKEND_URL;
    const full = url.startsWith('http') ? url : `${backend}${url}`;
    fetch(full, { headers: { Authorization: `Bearer ${localStorage.getItem('gf_token') || ''}` } })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (!blob) return;
        revoke = URL.createObjectURL(blob);
        setSrc(revoke);
      })
      .catch(() => {});
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [url]);

  if (!url) {
    return (
      <div className="rounded-lg border border-dashed border-[#D4AF37]/30 h-40 flex items-center justify-center text-[#808080] text-sm">
        Not uploaded
      </div>
    );
  }
  return (
    <div className="relative">
      {src ? (
        <img src={src} alt={alt} className="rounded-lg border border-[#D4AF37]/25 max-h-56 object-cover w-full" />
      ) : (
        <div className="rounded-lg border border-[#D4AF37]/25 h-40 flex items-center justify-center text-[#C0C0C0]">
          <FileImage size={22} className="mr-2" /> Loading…
        </div>
      )}
    </div>
  );
}

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/users/${id}`);
      setData(data);
    } catch {
      toast.error('Could not load user');
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) return <div className="p-8 text-[#C0C0C0]">Loading…</div>;
  const u = data.user;

  const toggleBlock = async () => {
    try {
      await api.patch(`/admin/users/${id}/block`, { blocked: !u.blocked });
      toast.success(u.blocked ? 'User unblocked' : 'User blocked');
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const setKyc = async (status) => {
    try {
      await api.patch(`/admin/users/${id}/kyc-verify`, { status });
      toast.success(`KYC ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setSending(true);
    try {
      await api.post(`/admin/users/${id}/message`, { subject, body });
      toast.success('Message sent');
      setSubject('');
      setBody('');
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-user-detail" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <button
        onClick={() => navigate('/admin')}
        className="text-sm text-[#C0C0C0] hover:text-[#D4AF37] inline-flex items-center gap-1 mb-6"
      >
        <ArrowLeft size={16} /> Back to users
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-2">User</p>
          <h1 className="font-serif-display text-4xl font-black text-gold-gradient">
            {u.first_name} {u.surname}
          </h1>
          <p className="mt-1 text-[#C0C0C0]">{u.email}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="toggle-block"
            onClick={toggleBlock}
            className={`btn-ghost-gold inline-flex items-center gap-2 !py-2 !px-4 text-sm ${
              u.blocked ? '!text-[#D4AF37] !border-[#D4AF37]' : ''
            }`}
          >
            {u.blocked ? <CircleCheck size={16} /> : <Ban size={16} />}
            {u.blocked ? 'Unblock' : 'Block'}
          </button>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card-embossed p-6 lg:col-span-2">
          <h3 className="font-serif-display text-xl text-white font-bold mb-4">Profile</h3>
          <dl className="grid grid-cols-2 gap-y-3 text-sm">
            <dt className="text-[#C0C0C0]/70">Age</dt>
            <dd className="text-white">{u.age}</dd>
            <dt className="text-[#C0C0C0]/70">Mobile</dt>
            <dd className="text-white">{u.mobile}</dd>
            <dt className="text-[#C0C0C0]/70">Email verified</dt>
            <dd>
              <Badge className={u.email_verified ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'bg-[#C0C0C0]/10 text-[#C0C0C0]'}>
                {u.email_verified ? 'Verified' : 'Pending'}
              </Badge>
            </dd>
            <dt className="text-[#C0C0C0]/70">Blocked</dt>
            <dd>
              <Badge className={u.blocked ? 'bg-[#991B1B]/30 text-[#F87171]' : 'bg-[#0a0a0a] text-[#C0C0C0]'}>
                {u.blocked ? 'Blocked' : 'Active'}
              </Badge>
            </dd>
            <dt className="text-[#C0C0C0]/70">Wallet</dt>
            <dd className="text-[#D4AF37] font-semibold">{formatZAR(u.wallet_balance || 0)}</dd>
            <dt className="text-[#C0C0C0]/70">ID number</dt>
            <dd className="text-white">{u.kyc?.id_number || '—'}</dd>
            <dt className="text-[#C0C0C0]/70">KYC status</dt>
            <dd>
              <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] capitalize">
                {KYC_LABEL[u.kyc?.status] || u.kyc?.status || '—'}
              </Badge>
            </dd>
          </dl>
        </div>

        <div className="card-embossed p-6">
          <h3 className="font-serif-display text-xl text-white font-bold mb-4">KYC Documents</h3>
          <div className="space-y-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C0C0C0]/70 mb-2">ID Document</p>
              <AuthedImage url={u.kyc?.id_doc_url} alt="ID doc" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C0C0C0]/70 mb-2">Selfie</p>
              <AuthedImage url={u.kyc?.selfie_url} alt="Selfie" />
            </div>
          </div>
          <div className="flex gap-2 mt-6">
            <button
              data-testid="kyc-verify-btn"
              onClick={() => setKyc('verified')}
              disabled={u.kyc?.status === 'verified' || u.kyc?.status === 'not_submitted'}
              className="btn-gold flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <ShieldCheck size={16} /> Verify
            </button>
            <button
              data-testid="kyc-reject-btn"
              onClick={() => setKyc('rejected')}
              disabled={u.kyc?.status === 'rejected' || u.kyc?.status === 'not_submitted'}
              className="btn-ghost-gold flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <ShieldX size={16} /> Reject
            </button>
          </div>
        </div>
      </div>

      {/* Bet history */}
      <div className="mt-10 card-embossed p-6">
        <h3 className="font-serif-display text-xl text-white font-bold mb-4">Bet history</h3>
        {data.tickets.length === 0 ? (
          <p className="text-sm text-[#C0C0C0]">No tickets.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#D4AF37]/15">
                  <TableHead className="text-[#C0C0C0]">Purchased</TableHead>
                  <TableHead className="text-[#C0C0C0]">Ticket</TableHead>
                  <TableHead className="text-[#C0C0C0]">Amount</TableHead>
                  <TableHead className="text-[#C0C0C0]">Draw</TableHead>
                  <TableHead className="text-[#C0C0C0]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.tickets.map((t) => (
                  <TableRow key={t.id} className="border-[#D4AF37]/10">
                    <TableCell className="text-white/90 text-sm">{new Date(t.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-[#D4AF37] text-sm font-mono">{t.ticket_number}</TableCell>
                    <TableCell className="text-white text-sm">{formatZAR(t.amount)}</TableCell>
                    <TableCell className="text-white/90 text-sm">{new Date(t.draw_date).toDateString()}</TableCell>
                    <TableCell>
                      <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] capitalize">{t.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Send message */}
      <div className="mt-10 card-embossed p-6">
        <h3 className="font-serif-display text-xl text-white font-bold mb-4">Send message</h3>
        <form onSubmit={sendMessage} className="space-y-3">
          <input
            data-testid="msg-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            className="w-full bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
          />
          <textarea
            data-testid="msg-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            placeholder="Type your message…"
            className="w-full bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
          />
          <button
            type="submit"
            data-testid="msg-send"
            disabled={sending}
            className="btn-gold inline-flex items-center gap-2"
          >
            <Send size={16} /> {sending ? 'Sending…' : 'Send message'}
          </button>
        </form>
      </div>
    </div>
  );
}
